/**
 * Custom-column cell for `people` columns (Monday-style assignees).
 *
 * Persists an array of workspace-user IDs (`string[]`). The cell renders the
 * selected users' avatars overlapping in the centre (up to 3 visible, the
 * rest collapsed into a `+N` chip). Clicking the cell opens a searchable
 * popover where the user can toggle assignees on/off.
 */
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { Check, Plus, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useCurrentWorkspaceId } from '@/entities/workspace';
import { workspaceUsersQuery, type WorkspaceUser } from '@/entities/project-member';
import { SearchInput } from '@/shared/ui/SearchInput';
import { getInitials } from '@/shared/utils/getInitials';
import { useTranslation } from 'react-i18next';

import { AvatarStack } from '../AvatarStack';

interface PeopleCustomCellProps {
  /** Persisted list of user IDs assigned to this brief on this column. */
  value: string[] | undefined;
  /** `null` clears the assignment entirely. */
  onChange: (next: string[] | null) => void;
}

export function PeopleCustomCell({ value, onChange }: PeopleCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const workspaceId = useCurrentWorkspaceId();

  const selectedIds = useMemo(() => value ?? [], [value]);

  // We need the workspace user list both when the popover is open (to render
  // the picker) AND whenever the cell has any assigned IDs to display (to map
  // them to names + avatars — without this the AvatarStack falls back to the
  // "?" placeholder). TanStack Query dedupes by `queryKey`, so all
  // PeopleCustomCell instances across the board share a single network
  // request — no fetch storm.
  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId) && (open || selectedIds.length > 0),
  });

  const allUsers: WorkspaceUser[] = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data]);

  const selectedUsers = useMemo(
    () => allUsers.filter((u) => selectedIds.includes(u.id)),
    [allUsers, selectedIds],
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(
      (u) => u.name.toLowerCase().includes(q) || (u.email?.toLowerCase().includes(q) ?? false),
    );
  }, [allUsers, search]);

  const toggleUser = (userId: string) => {
    const next = selectedIds.includes(userId)
      ? selectedIds.filter((id) => id !== userId)
      : [...selectedIds, userId];
    onChange(next.length === 0 ? null : next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex h-7 w-full items-center justify-center rounded text-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {selectedIds.length === 0 ? (
            <UserPlus
              className="size-4 text-muted-foreground"
              aria-label={t('trackersBoard.peopleCustomCell.assignPeople')}
            />
          ) : (
            <AvatarStack ids={selectedIds} users={allUsers} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" onClick={(e) => e.stopPropagation()}>
        <SearchInput
          aria-label={t('trackersBoard.peopleCustomCell.searchPeopleLabel')}
          placeholder={t('trackersBoard.peopleCustomCell.searchPeople')}
          value={search}
          onChange={setSearch}
          className="mb-2"
        />
        <div className="max-h-64 overflow-y-auto">
          {usersQuery.isLoading ? (
            <ul className="flex flex-col gap-0.5" aria-hidden>
              {['s1', 's2', 's3'].map((key) => (
                <li key={key} className="flex items-center gap-2 px-2 py-1.5">
                  <Skeleton className="size-6 shrink-0 rounded-full" />
                  <Skeleton className="h-3 w-32 rounded-sm" />
                </li>
              ))}
            </ul>
          ) : filteredUsers.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              {t('trackersBoard.peopleCustomCell.noPeopleFound')}
            </div>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {filteredUsers.map((user) => {
                const isSelected = selectedIds.includes(user.id);
                return (
                  <li key={user.id}>
                    <button
                      type="button"
                      onClick={() => toggleUser(user.id)}
                      className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs transition-colors hover:bg-accent"
                    >
                      <Avatar className="size-6 shrink-0">
                        {user.avatarUrl ? (
                          <AvatarImage src={user.avatarUrl} alt={user.name} />
                        ) : null}
                        <AvatarFallback className="text-[10px]">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium text-foreground">{user.name}</span>
                        {user.email ? (
                          <span className="truncate text-[10px] text-muted-foreground">
                            {user.email}
                          </span>
                        ) : null}
                      </div>
                      {isSelected ? (
                        <Check className="size-3.5 shrink-0 text-primary" aria-hidden />
                      ) : (
                        <Plus className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {selectedUsers.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="mt-1 w-full rounded px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-accent"
          >
            {t('trackersBoard.peopleCustomCell.clearAll')}
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
